import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import { usePermission } from "../../hooks/usePermissions";
import DataTable from "../../components/common/DataTable/DataTable";
import Button from "../../components/common/Button/Button";
import Input from "../../components/common/Input/Input";
import Modal from "../../components/common/Modal/Modal";
import { referralService } from "../../services/referralService";
import styles from "./ReferralProgram.module.css";
import { formatAdminDate, formatAdminDateTime } from "../../utils/datetime";

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const PAGE_SIZE = 10;

const ReferralProgram = () => {
  const { hasPermission } = usePermission();

  // State: Dashboard Cards
  const [dashboard, setDashboard] = useState({
    successfulReferrals: 0,
    pendingReferrals: 0,
    totalPointsIssued: 0,
    activeScheme: "None",
    totalWalletBalance: 0
  });

  // State: Program Settings
  const [program, setProgram] = useState(null);

  // State: Relationships Table
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // State: Filters
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // State: Modals
  const [viewModalData, setViewModalData] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  const [schemes, setSchemes] = useState([]);
  const [isCreateSchemeModalOpen, setIsCreateSchemeModalOpen] = useState(false);
  const [newSchemeData, setNewSchemeData] = useState({ name: "", referrerPoints: 100, refereePoints: 100, startDate: "", endDate: "" });
  const [editSchemeData, setEditSchemeData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchProgram();
    fetchSchemes();
    fetchRelationships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearch, statusFilter]);

  const fetchDashboard = async () => {
    try {
      const res = await referralService.getDashboard();
      if (res?.success) {
        setDashboard(res.data);
      }
    } catch (err) {
      console.error("Dashboard fetch error", err);
    }
  };

  const fetchSchemes = async () => {
    try {
      const res = await referralService.getAllSchemes();
      if (res?.success) {
        setSchemes(res.data);
      }
    } catch (err) {
      console.error("Schemes fetch error", err);
    }
  };

  const fetchProgram = async () => {
    try {
      const res = await referralService.getProgram();
      if (res?.success) {
        setProgram(res.data);
      }
    } catch (err) {
      console.error("Program fetch error", err);
    }
  };

  const fetchRelationships = async () => {
    setLoading(true);
    try {
      const res = await referralService.getRelationships({
        page,
        limit: PAGE_SIZE,
        search: appliedSearch,
        status: statusFilter
      });
      if (res?.success) {
        setRelationships(res.data);
        setTotalRecords(res.pagination?.total || 0);
      }
    } catch (err) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (id) => {
    try {
      const res = await referralService.getRelationshipDetail(id);
      if (res?.success) {
        setViewModalData(res.data);
        setIsViewModalOpen(true);
      } else {
        toast.error("Failed to load details");
      }
    } catch (err) {
      toast.error("Error loading details");
    }
  };

  const handleEditSave = async () => {
    if (!editSchemeData) return;
    setIsSaving(true);
    try {
      const res = await referralService.updateScheme(editSchemeData.id, {
        scheme_code: editSchemeData.name,
        referring_user_benifit: editSchemeData.referrerPoints,
        refree_user_benifit: editSchemeData.refereePoints,
        status: editSchemeData.status,
        start_date: editSchemeData.startDate || null,
        end_date: editSchemeData.endDate || null
      });
      if (res?.success) {
        toast.success("Scheme updated");
        setIsEditModalOpen(false);
        fetchDashboard();
        fetchProgram();
        fetchSchemes();
      } else {
        toast.error("Failed to update scheme");
      }
    } catch (err) {
      toast.error("Error updating scheme");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateScheme = async () => {
    if (!newSchemeData.name) {
      toast.error("Scheme Name is required");
      return;
    }
    setIsSaving(true);
    try {
      const res = await referralService.createScheme({
        scheme_code: newSchemeData.name,
        referring_user_benifit: newSchemeData.referrerPoints,
        refree_user_benifit: newSchemeData.refereePoints,
        status: "inactive",
        start_date: newSchemeData.startDate || null,
        end_date: newSchemeData.endDate || null
      });
      if (res?.success) {
        toast.success("Scheme created successfully");
        setIsCreateSchemeModalOpen(false);
        setNewSchemeData({ name: "", referrerPoints: 100, refereePoints: 100, startDate: "", endDate: "" });
        fetchSchemes();
      } else {
        toast.error("Failed to create scheme");
      }
    } catch (err) {
      toast.error("Error creating scheme");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleSchemeStatus = async (scheme) => {
    const newStatus = scheme.status === "active" ? "inactive" : "active";
    try {
      const res = await referralService.updateScheme(scheme.id, {
        status: newStatus
      });
      if (res?.success) {
        toast.success(`Scheme ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchDashboard();
        fetchProgram();
        fetchSchemes();
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      toast.error("Error updating status");
    }
  };

  const handleDeleteScheme = async (id) => {
    if (window.confirm("Are you sure you want to delete this scheme?")) {
      try {
        const res = await referralService.deleteScheme(id);
        if (res?.success) {
          toast.success("Scheme deleted successfully");
          fetchDashboard();
          fetchProgram();
          fetchSchemes();
        } else {
          toast.error(res?.message || "Failed to delete scheme");
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Error deleting scheme");
      }
    }
  };

  const getStatusClass = (status) => {
    if (!status) return styles.statusDefault;
    const s = status.toLowerCase();
    if (s === "rewarded" || s === "active" || s === "enabled") return styles.statusSuccess;
    if (s === "pending" || s === "inactive" || s === "disabled") return styles.statusWarning;
    if (s === "rejected" || s === "expired" || s === "inactive") return styles.statusError;
    return styles.statusDefault;
  };

  const columns = [
    { key: "referrer_name", label: "Referrer", render: (val) => val || "N/A" },
    { key: "referral_code_used", label: "Referral Code", render: (val) => val || "N/A" },
    { key: "referee_name", label: "Referred User", render: (val) => val || "N/A" },
    { 
      key: "referral_status", 
      label: "Status",
      render: (val) => (
        <span className={`${styles.statusBadge} ${getStatusClass(val)}`}>
          {val || "Unknown"}
        </span>
      )
    },
    { key: "referrer_reward", label: "Referrer Reward", render: (val) => val ? `${val} pts` : "-" },
    { key: "referee_reward", label: "Referee Reward", render: (val) => val ? `${val} pts` : "-" },
    { 
      key: "created_at", 
      label: "Created Date", 
      render: (val) => formatAdminDate(val, "N/A") 
    },
    {
      key: "actions",
      label: "Action",
      render: (_, row) => (
        <button className={styles.actionBtn} onClick={() => handleView(row.id)}>
          View
        </button>
      )
    }
  ];

  const schemeColumns = [
    { key: "id", label: "ID" },
    { key: "scheme_code", label: "Scheme Name" },
    { key: "referring_user_benifit", label: "Referrer Pts" },
    { key: "refree_user_benifit", label: "Referee Pts" },
    { 
      key: "status", 
      label: "Status",
      render: (val, row) => (
        <span className={`${styles.statusBadge} ${getStatusClass(val)}`}>
          {(val || '').toUpperCase()}
        </span>
      )
    },
    { 
      key: "start_date", 
      label: "Start Date", 
      render: (val) => formatAdminDate(val, "-") 
    },
    { 
      key: "end_date", 
      label: "End Date", 
      render: (val) => formatAdminDate(val, "-") 
    },
    { 
      key: "created_at", 
      label: "Created On", 
      render: (val) => formatAdminDate(val) 
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label className={styles.switch} title={row.status === "active" ? "Deactivate" : "Activate"}>
            <input 
              type="checkbox" 
              checked={row.status === "active"} 
              onChange={() => handleToggleSchemeStatus(row)} 
            />
            <span className={styles.slider}></span>
          </label>
          <button
            className={styles.actionBtn}
            style={{ padding: '6px', color: '#6b7280', display: 'flex' }}
            title="Edit"
            onClick={() => {
              setEditSchemeData({
                id: row.id,
                name: row.scheme_code,
                referrerPoints: row.referring_user_benifit,
                refereePoints: row.refree_user_benifit,
                status: row.status,
                startDate: row.start_date ? row.start_date.substring(0, 16) : "",
                endDate: row.end_date ? row.end_date.substring(0, 16) : ""
              });
              setIsEditModalOpen(true);
            }}
          >
            <EditIcon />
          </button>
          <button
            className={styles.actionBtn}
            style={{ padding: '6px', color: '#ef4444', display: 'flex' }}
            title="Delete"
            onClick={() => handleDeleteScheme(row.id)}
          >
            <TrashIcon />
          </button>
        </div>
      )
    }
  ];

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-right" autoClose={2000} />

      <div className={styles.pageHeader}>
        <div className={styles.topRow}>
          <div>
            <h2>Referral Program</h2>
            <p className={styles.pageSubtitle}>Manage referrals, rewards, and schemes</p>
          </div>
        </div>
      </div>

      {/* 1. Summary Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Successful Referrals</span>
          <span className={styles.statValue}>{dashboard.successfulReferrals}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Pending Referrals</span>
          <span className={styles.statValue}>{dashboard.pendingReferrals}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Total Points Issued</span>
          <span className={styles.statValue}>{dashboard.totalPointsIssued}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Active Scheme</span>
          <span className={styles.statValue}>{dashboard.activeScheme}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Total Wallet Balance</span>
          <span className={styles.statValue}>{dashboard.totalWalletBalance}</span>
        </div>
      </div>

      {/* 2. Referral Schemes Management */}
      <div className={styles.settingsCard}>
        <div className={styles.settingsHeader}>
          <h3>Referral Schemes</h3>
          <Button 
            variant="primary" 
            onClick={() => setIsCreateSchemeModalOpen(true)}
          >
            + Create Scheme
          </Button>
        </div>
        <div className={styles.schemesTableWrap}>
          <DataTable
            columns={schemeColumns}
            data={schemes}
          />
        </div>
      </div>

      {/* 3. Search & Filters */}
      <div className={styles.filterRow}>
        <div className={styles.searchGroup}>
          <Input
            className={styles.searchInputWrap}
            placeholder="Search by Name, Phone, Code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setAppliedSearch(search); setPage(1); } }}
          />
          <Button onClick={() => { setAppliedSearch(search); setPage(1); }}>Search</Button>
        </div>
        <div>
          <select 
            value={statusFilter} 
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="rewarded">Rewarded</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* 4. Referral Table */}
      {loading ? (
        <p className={styles.loadingState}>Loading...</p>
      ) : (
        <div className={styles.tableCard}>
          <DataTable
            columns={columns}
            data={relationships}
          />
          
          <div className={styles.pagination}>
            <div className={styles.paginationInfo}>
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, totalRecords)} of {totalRecords} entries
            </div>
            <div className={styles.paginationControls}>
              <Button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                variant="secondary"
                size="small"
              >
                &lt;
              </Button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                if (
                  p === 1 ||
                  p === totalPages ||
                  (p >= page - 1 && p <= page + 1)
                ) {
                  return (
                    <button
                      key={p}
                      className={`${styles.pageBtn} ${page === p ? styles.active : ""}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  );
                }
                if (p === page - 2 || p === page + 2) {
                  return <span key={p} className={styles.paginationEllipsis}>...</span>;
                }
                return null;
              })}

              <Button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                variant="secondary"
                size="small"
              >
                &gt;
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 5. View Referral Modal */}
      {viewModalData && (
        <Modal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          title="Referral Details"
          size="large"
          closeOnOverlay={false}
        >
          <div className={styles.modalGrid}>
            <div className={styles.fullWidth}>
              <h4 className={styles.modalSectionTitle}>Referral Information</h4>
              <p><strong>ID:</strong> {viewModalData.referral.id}</p>
              <p><strong>Code Used:</strong> {viewModalData.referral.referral_code_used}</p>
              <p>
                <strong>Status:</strong>{" "}
                <span className={`${styles.statusBadge} ${getStatusClass(viewModalData.referral.referral_status)}`}>
                  {viewModalData.referral.referral_status}
                </span>
              </p>
              <p><strong>Created:</strong> {formatAdminDateTime(viewModalData.referral.created_at)}</p>
              {viewModalData.referral.rewarded_at && (
                <p><strong>Rewarded:</strong> {formatAdminDateTime(viewModalData.referral.rewarded_at)}</p>
              )}
            </div>

            <div>
              <h4 className={styles.modalSectionTitle}>Referrer Information</h4>
              <p><strong>Name:</strong> {viewModalData.referral.referrer_name}</p>
              <p><strong>Phone:</strong> {viewModalData.referral.referrer_phone}</p>
              <p><strong>Email:</strong> {viewModalData.referral.referrer_email}</p>
              <p><strong>Wallet Balance:</strong> {viewModalData.referrerWallet.available_balance} pts</p>
            </div>

            <div>
              <h4 className={styles.modalSectionTitle}>Referred User Information</h4>
              <p><strong>Name:</strong> {viewModalData.referral.referee_name}</p>
              <p><strong>Phone:</strong> {viewModalData.referral.referee_phone}</p>
              <p><strong>Email:</strong> {viewModalData.referral.referee_email}</p>
              <p><strong>Wallet Balance:</strong> {viewModalData.refereeWallet.available_balance} pts</p>
            </div>

            <div className={styles.fullWidth}>
              <h4 className={styles.modalSectionTitle}>Related Transactions</h4>
              {viewModalData.transactions.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '8px 4px' }}>User ID</th>
                      <th style={{ padding: '8px 4px' }}>Points</th>
                      <th style={{ padding: '8px 4px' }}>Desc</th>
                      <th style={{ padding: '8px 4px' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewModalData.transactions.map(t => (
                      <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px 4px' }}>{t.user_id}</td>
                        <td style={{ padding: '8px 4px', color: t.points >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                          {t.points >= 0 ? '+' : ''}{t.points}
                        </td>
                        <td style={{ padding: '8px 4px' }}>{t.description}</td>
                        <td style={{ padding: '8px 4px' }}>{formatAdminDate(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: '#6b7280' }}>No transactions found.</p>
              )}
            </div>

            <div className={styles.fullWidth}>
              <h4 className={styles.modalSectionTitle}>Technical Information</h4>
              <p><strong>Source:</strong> {viewModalData.referral.referral_source || 'N/A'}</p>
              <p><strong>IP Address:</strong> {viewModalData.referral.device_ip || 'N/A'}</p>
              <p><strong>Device ID:</strong> {viewModalData.referral.device_id || 'N/A'}</p>
            </div>
          </div>
        </Modal>
      )}

      {/* 6. Edit Scheme Modal */}
      {editSchemeData && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Referral Scheme"
          closeOnOverlay={false}
        >
          <div className={styles.formGrid}>
            <Input
              label="Scheme Name"
              value={editSchemeData.name}
              onChange={(e) => setEditSchemeData({ ...editSchemeData, name: e.target.value })}
            />
            <Input
              label="Referrer Points"
              type="number"
              value={editSchemeData.referrerPoints}
              onChange={(e) => setEditSchemeData({ ...editSchemeData, referrerPoints: parseInt(e.target.value) || 0 })}
            />
            <Input
              label="Referee Points"
              type="number"
              value={editSchemeData.refereePoints}
              onChange={(e) => setEditSchemeData({ ...editSchemeData, refereePoints: parseInt(e.target.value) || 0 })}
            />
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Status</label>
              <select 
                value={editSchemeData.status} 
                onChange={(e) => setEditSchemeData({ ...editSchemeData, status: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Activation Date (Optional)</label>
              <Input 
                type="datetime-local"
                value={editSchemeData.startDate}
                onChange={(e) => setEditSchemeData({...editSchemeData, startDate: e.target.value})}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Deactivation Date (Optional)</label>
              <Input 
                type="datetime-local"
                value={editSchemeData.endDate}
                onChange={(e) => setEditSchemeData({...editSchemeData, endDate: e.target.value})}
              />
            </div>
          </div>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} loading={isSaving}>Save Changes</Button>
          </div>
        </Modal>
      )}

      {/* 7. Create Scheme Modal */}
      {isCreateSchemeModalOpen && (
        <Modal 
          isOpen={isCreateSchemeModalOpen} 
          onClose={() => setIsCreateSchemeModalOpen(false)}
          title="Create New Referral Scheme"
          closeOnOverlay={false}
        >
          <div className={styles.modalContent}>
            <div className={styles.formGroup}>
              <label>Scheme Name / Code</label>
              <Input 
                value={newSchemeData.name}
                onChange={(e) => setNewSchemeData({...newSchemeData, name: e.target.value})}
                placeholder="e.g. SUMMER2026"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Referrer Reward Points</label>
              <Input 
                type="number"
                value={newSchemeData.referrerPoints}
                onChange={(e) => setNewSchemeData({...newSchemeData, referrerPoints: parseInt(e.target.value) || 0})}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Referee Reward Points</label>
              <Input 
                type="number"
                value={newSchemeData.refereePoints}
                onChange={(e) => setNewSchemeData({...newSchemeData, refereePoints: parseInt(e.target.value) || 0})}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Activation Date (Optional)</label>
              <Input 
                type="datetime-local"
                value={newSchemeData.startDate}
                onChange={(e) => setNewSchemeData({...newSchemeData, startDate: e.target.value})}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Deactivation Date (Optional)</label>
              <Input 
                type="datetime-local"
                value={newSchemeData.endDate}
                onChange={(e) => setNewSchemeData({...newSchemeData, endDate: e.target.value})}
              />
            </div>
          </div>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setIsCreateSchemeModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreateScheme} disabled={isSaving}>
              {isSaving ? "Creating..." : "Create Scheme"}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ReferralProgram;
